import {
  Badge,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Archive,
  CircleCheck,
  CircleSlash,
  GitBranch,
  History,
  Plus,
  RotateCcw,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { taxonomyListChangeLogQueryOptions } from '@/entities/taxonomy-list';
import type { TaxonomyChangeAction } from '@/entities/taxonomy';
import { describeMetadata } from './describeMetadata';

export interface ListHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
  /** Fallback when the actor is the current user (Platform lookup is unavailable locally). */
  currentUserId?: string;
  currentUserName?: string | null;
  /** Jump to a version in the editor — the trail's entries are navigable. */
  onViewVersion?: (versionId: string) => void;
}

/**
 * Visual treatment per audit action. Lists only emit this subset.
 *
 * Getters, not values: a resolved label would freeze at import.
 */
const ACTION_META: Partial<Record<TaxonomyChangeAction, { label: string; icon: LucideIcon }>> = {
  created: {
    get label() {
      return i18n.t('knowledge:taxonomy.listHistoryDrawer.listCreated');
    },
    icon: Plus,
  },
  version_created: {
    get label() {
      return i18n.t('knowledge:taxonomy.listHistoryDrawer.newVersion');
    },
    icon: GitBranch,
  },
  restored: {
    get label() {
      return i18n.t('knowledge:taxonomy.listHistoryDrawer.restored');
    },
    icon: RotateCcw,
  },
  published: {
    get label() {
      return i18n.t('knowledge:taxonomy.listHistoryDrawer.published');
    },
    icon: CircleCheck,
  },
  unpublished: {
    get label() {
      return i18n.t('knowledge:taxonomy.listHistoryDrawer.unpublished');
    },
    icon: CircleSlash,
  },
  archived: {
    get label() {
      return i18n.t('knowledge:taxonomy.listHistoryDrawer.archived');
    },
    icon: Archive,
  },
  deleted: {
    get label() {
      return i18n.t('knowledge:taxonomy.listHistoryDrawer.deleted');
    },
    icon: Trash2,
  },
  updated: {
    get label() {
      return i18n.t('knowledge:taxonomy.listHistoryDrawer.updated');
    },
    icon: History,
  },
};

/**
 * The list's audit trail — who moved this vocabulary, when, and why.
 *
 * A list needs this more than a schema does: editing a schema changes one
 * schema, while editing a list changes every schema that follows it.
 *
 * Deliberately separate from the schema `HistoryDrawer` rather than a
 * generalisation of it: that one is built around field/rule diffing, which is a
 * different question from "which vocabulary version was live when".
 */
export function ListHistoryDrawer(props: ListHistoryDrawerProps) {
  const { t } = useTranslation('knowledge');
  const { open, onOpenChange, listId, currentUserId, currentUserName, onViewVersion } = props;

  const query = useQuery({ ...taxonomyListChangeLogQueryOptions(listId), enabled: open });
  const entries = query.data ?? [];

  const actorLabel = (actorId: string) =>
    actorId === currentUserId
      ? (currentUserName ?? t('taxonomy.listHistoryDrawer.you'))
      : t('taxonomy.listHistoryDrawer.teamMember');

  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex h-full w-full flex-col bg-card sm:!max-w-[440px]">
        <DrawerHeader className="flex flex-row items-center justify-between border-b border-border-light">
          <DrawerTitle className="flex items-center gap-2 text-base">
            <History className="size-4" aria-hidden />
            {t('taxonomy.listHistoryDrawer.history')}
          </DrawerTitle>
          <DrawerClose aria-label={t('taxonomy.listHistoryDrawer.closeHistory')}>
            <X className="size-4" />
          </DrawerClose>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : query.isError ? (
            // Without this, a 403 or a 500 on an audit trail reads as "nothing
            // has ever happened to this list" — the one answer a change log must
            // never give when it does not actually know.
            <p className="py-10 text-center text-sm text-muted-foreground">
              {t('taxonomy.listHistoryDrawer.couldnTLoadHistory')}
            </p>
          ) : entries.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {t('taxonomy.listHistoryDrawer.noChangesRecordedYet')}
            </p>
          ) : (
            <ol className="flex flex-col gap-4">
              {entries.map((entry) => {
                const meta = ACTION_META[entry.action] ?? { label: entry.action, icon: History };
                const Icon = meta.icon;
                const detail = describeMetadata(entry);
                return (
                  <li key={entry.id} className="flex gap-3">
                    <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-surface text-muted-foreground">
                      <Icon className="size-3.5" aria-hidden />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{meta.label}</span>
                        {detail && (
                          <Badge
                            variant="ghost"
                            roundness="full"
                            className="border-border-light text-[10px]"
                          >
                            {detail}
                          </Badge>
                        )}
                      </div>
                      <p className="break-words text-sm text-muted-foreground">{entry.reason}</p>
                      <span className="text-xs text-muted-foreground">
                        {actorLabel(entry.actorId)} ·{' '}
                        <time
                          dateTime={entry.createdAt}
                          // eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern
                          title={format(new Date(entry.createdAt), 'PPpp')}
                        >
                          {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
                        </time>
                      </span>
                      {entry.versionId && onViewVersion && (
                        <button
                          type="button"
                          onClick={() => onViewVersion(entry.versionId!)}
                          className="self-start text-xs text-primary hover:underline"
                        >
                          {t('taxonomy.listHistoryDrawer.viewVersion')}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

ListHistoryDrawer.displayName = 'ListHistoryDrawer';
